/**
 * Aides communes des écrans « Ouvertures » (ARTO-128). Rien ici ne calcule un
 * état ou un numéro de dossier : l'API les rend, l'écran les affiche.
 */
import type { TFunction } from 'i18next';
import type { BadgeTone } from '@artosera/ui';
import { ApiError } from './api.js';
import { estElevationNonAcceptee } from './elevation.js';
import { raisonErreur } from './modules.js';
import type {
  DossierOuverture,
  EtapeCreationOuverture,
  EtapeOuverture,
  EtatOuverture,
  LotRecette,
} from '../types.js';

/** Les états dans l'ordre du parcours (la frise de la fiche). */
export const ETATS_PARCOURS = [
  'brouillon',
  'contrat_signe',
  'provisionne',
  'reprise',
  'recette',
  'bascule_planifiee',
  'en_service',
] as const satisfies readonly EtatOuverture[];

export const ETATS_OUVERTURE_TOUS: readonly EtatOuverture[] = [...ETATS_PARCOURS, 'abandonne'];

export const TONE_ETAT_OUVERTURE: Record<EtatOuverture, BadgeTone> = {
  brouillon: 'neutral',
  contrat_signe: 'deposit',
  provisionne: 'reserved',
  reprise: 'reserved',
  recette: 'reserved',
  bascule_planifiee: 'accent',
  en_service: 'available',
  abandonne: 'withdrawn',
};

/** Les sous-étapes de la création, dans l'ordre de l'API. */
export const SOUS_ETAPES: readonly EtapeCreationOuverture[] = [
  'provisionnement',
  'modules',
  'addons',
  'domaine',
  'invitations',
  'prereglages',
];

export const ETATS_CLOS_OUVERTURE: readonly EtatOuverture[] = ['en_service', 'abandonne'];

export function estClos(etat: EtatOuverture): boolean {
  return ETATS_CLOS_OUVERTURE.includes(etat);
}

const JOUR_MS = 86_400_000;

/**
 * Jours de retard d'une bascule prévue : 0 tant que le jour prévu n'est pas
 * écoulé, et 0 pour un dossier qui n'attend plus rien (en service, abandonné).
 */
export function joursDeRetard(
  basculePrevueLe: string | null,
  etat: EtatOuverture,
  maintenant: number = Date.now(),
): number {
  if (basculePrevueLe === null || etat === 'en_service' || etat === 'abandonne') return 0;
  const debut = new Date(basculePrevueLe).getTime();
  if (Number.isNaN(debut)) return 0;
  const ecart = maintenant - (debut + JOUR_MS);
  return ecart > 0 ? Math.ceil(ecart / JOUR_MS) : 0;
}

/** Un jour lisible : « 30 nov. 2026 ». Les dates du dossier sont des jours, sans heure. */
export function formaterJour(valeur: string): string {
  return new Date(valeur).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Une date `AAAA-MM-JJ` (ou ISO) pour un champ `type=date`. */
export function dateEnSaisie(valeur: string | null): string {
  return valeur === null ? '' : valeur.slice(0, 10);
}

/** La sous-étape cochée dans le dossier, ou `undefined` si elle reste à faire. */
export function etapeCochee(
  dossier: Pick<DossierOuverture, 'etapes'>,
  cle: EtapeCreationOuverture,
): EtapeOuverture | undefined {
  return dossier.etapes.find((etape) => etape.cle === cle);
}

/**
 * L'étape de l'assistant où reprendre un brouillon : la première dont une
 * donnée indispensable manque. L'offre et les préréglages ont des valeurs
 * par défaut ; on y revient par le menu des étapes.
 */
export function etapeDeReprise(dossier: DossierOuverture): number {
  const { identite, equipe, contrat } = dossier;
  if (identite.slug === '') return 1;
  if (identite.estTest) return equipe.direction === null ? 1 : 6;
  if (contrat.reference === '' || contrat.signeLe === null) return 3;
  if (equipe.direction === null) return 4;
  return 6;
}

/** Le message d'une erreur de l'API, dans la langue de l'écran. */
export function messageErreurOuverture(t: TFunction, erreur: unknown): string {
  const dire = (cle: string, options?: Record<string, unknown>): string =>
    String(t(`ouvertures.erreurs.${cle}`, options));

  if (estElevationNonAcceptee(erreur)) return dire('mfa');
  if (!(erreur instanceof ApiError)) return dire('reseau');

  const details = (erreur.details ?? {}) as {
    champs?: unknown;
    manquants?: unknown;
    lots?: unknown;
  };
  const raison = raisonErreur(erreur);
  const connues = [
    'dossier_clos',
    'slug_fige',
    'slug_pris',
    'contrat_incomplet',
    'provisionnement_incomplet',
    'transition_interdite',
    'bascule_requise',
    'reprise_en_cours',
    'galerie_absente',
    'validation_requise',
    'bascule_non_datee',
    'recette_non_ouverte',
    'invitations_non_envoyees',
    'delta_inacheve',
    'bascule_en_cours',
    'mandat_concurrent',
    'lot_modifie',
    'creation_en_cours',
    'etat_incompatible',
    'module_non_disponible',
    'addon_echeance_passee',
    'membre_en_double',
    'role_plateforme_existant',
    'groupe_inconnu',
    'domaine_pris',
  ];
  if (raison === 'dossier_incomplet') {
    const manquants = Array.isArray(details.manquants) ? details.manquants.map(String) : [];
    return dire('dossier_incomplet', { manquants: manquants.join(', ') });
  }
  if (raison === 'lots_non_termines') {
    const lots = Array.isArray(details.lots) ? (details.lots as LotRecette[]) : [];
    const noms = lots.map(
      (lot) =>
        `${String(t(`reprise.natures.${lot.nature}`, { defaultValue: lot.nature }))} (${String(
          t(`reprise.etats.${lot.etat}`, { defaultValue: lot.etat }),
        )})`,
    );
    return dire('lots_non_termines', { lots: noms.join(', ') });
  }
  if (raison !== null && connues.includes(raison)) return dire(raison);
  if (erreur.status === 400 || erreur.code === 'validation.invalide') {
    const champs = Array.isArray(details.champs)
      ? (details.champs as Array<{ chemin?: string }>)
          .map((champ) => champ.chemin)
          .filter((chemin): chemin is string => typeof chemin === 'string' && chemin !== '')
      : [];
    return champs.length > 0
      ? dire('invalide', { champs: champs.join(', ') })
      : dire('invalideSansChamp');
  }
  if (erreur.status === 403) return dire('droits');
  return erreur.message || dire('generique');
}

/** Vrai si le dossier a changé côté serveur (révision périmée). */
export function estRevisionPerimee(erreur: unknown): boolean {
  return (
    erreur instanceof ApiError &&
    erreur.status === 409 &&
    raisonErreur(erreur) === 'revision_ouverture_perimee'
  );
}

export const CLE_OUVERTURES = ['console', 'ouvertures'] as const;
export const cleOuverture = (id: string): readonly unknown[] => ['console', 'ouverture', id];
